import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/diagrams/RichTextEditor";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { RefreshCw, Mail, Paperclip, Download, X, Link2, Search, Plus, Trash2, ChevronLeft } from "lucide-react";

/**
 * Reusable Email list component.
 * - When `entityType` + `entityId` are set, lists threads linked to that entity only.
 * - Otherwise lists all workspace threads (global inbox).
 */
export default function EmailsTab({ entityType, entityId, embedded = false, defaultCompose = false, defaultInherited = false }) {
  const { activeWorkspaceId } = useAuth();
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [viewAll, setViewAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [hasAtt, setHasAtt] = useState(false);
  const [inheritedOnly, setInheritedOnly] = useState(defaultInherited);
  const [mailbox, setMailbox] = useState("all"); // 'all' | 'personal' | 'shared'
  const [channel, setChannel] = useState("all"); // 'all' | 'gmail' | 'resend_inbound' | 'whatsapp'
  const cacheRef = useRef({}); // key → {threads, total, viewAll}
  const [openThread, setOpenThread] = useState(null);
  const [composeOpen, setComposeOpen] = useState(defaultCompose);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    const key = JSON.stringify({ entityType, entityId, search, onlyMine, hasAtt, inheritedOnly, mailbox, channel });
    const cached = cacheRef.current[key];
    if (cached) {
      setThreads(cached.threads);
      setTotal(cached.total);
      setViewAll(cached.viewAll);
      return;
    }
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (entityType && entityId) { params.entity_type = entityType; params.entity_id = entityId; }
      if (search.trim()) params.search = search.trim();
      if (onlyMine) params.only_mine = true;
      if (hasAtt) params.has_attachments = true;
      if (inheritedOnly) params.inherited = true;
      if (mailbox !== "all") params.mailbox = mailbox;
      if (channel !== "all") params.channel = channel;
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/email-threads`, { params });
      const payload = { threads: data.threads || [], total: data.total || 0, viewAll: !!data.view_all };
      cacheRef.current[key] = payload;
      setThreads(payload.threads);
      setTotal(payload.total);
      setViewAll(payload.viewAll);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load emails");
    } finally { setBusy(false); }
  }, [activeWorkspaceId, entityType, entityId, search, onlyMine, hasAtt, inheritedOnly, mailbox, channel]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/gmail/refresh`);
      toast.success(`Synced ${data.messages_added || 0} new message${data.messages_added === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Refresh failed — is Gmail connected?");
    } finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-3" data-testid="emails-tab">
      {!embedded && !entityType && (
        <div className="flex items-center gap-1 border border-border p-1 w-fit" data-testid="mailbox-tabs">
          {[
            ["all", "All mail"],
            ["personal", "My mail"],
            ["shared", "Shared inbox"],
          ].map(([v, lbl]) => (
            <button key={v} onClick={() => setMailbox(v)}
              className={`px-3 py-1 text-xs uppercase tracking-widest ${mailbox === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`mailbox-tab-${v}`}>
              {lbl}
            </button>
          ))}
        </div>
      )}
      {!embedded && !entityType && (
        <div className="flex items-center gap-1 border border-border p-1 w-fit" data-testid="channel-tabs">
          {[
            ["all", "All channels"],
            ["gmail", "Gmail"],
            ["resend_inbound", "System email"],
            ["whatsapp", "WhatsApp"],
          ].map(([v, lbl]) => (
            <button key={v} onClick={() => setChannel(v)}
              className={`px-3 py-1 text-xs uppercase tracking-widest ${channel === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`channel-tab-${v}`}>
              {lbl}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input placeholder="Search subject…" value={search} onChange={(e) => setSearch(e.target.value)}
                 className="pl-8 h-9 rounded-none" data-testid="emails-search" />
        </div>
        <label className="text-xs inline-flex items-center gap-1.5">
          <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} data-testid="emails-only-mine" />
          Only mine
        </label>
        <label className="text-xs inline-flex items-center gap-1.5">
          <input type="checkbox" checked={hasAtt} onChange={(e) => setHasAtt(e.target.checked)} data-testid="emails-has-attachments" />
          With attachments
        </label>
        <label className="text-xs inline-flex items-center gap-1.5" title="Threads reassigned to you when a teammate was off-boarded">
          <input type="checkbox" checked={inheritedOnly} onChange={(e) => setInheritedOnly(e.target.checked)} data-testid="emails-inherited-only" />
          Inherited
        </label>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={refresh} disabled={refreshing} data-testid="emails-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Syncing…" : "Refresh"}
          </Button>
          <Button size="sm" className="rounded-none h-8" onClick={() => setComposeOpen(true)} data-testid="emails-compose">
            <Plus className="w-3.5 h-3.5 mr-1" /> Compose
          </Button>
        </div>
      </div>

      {viewAll && !embedded && (
        <div className="text-[11px] uppercase tracking-widest text-emerald-700 border border-emerald-200 bg-emerald-50 inline-block px-2 py-0.5">
          Admin — seeing every thread in the workspace
        </div>
      )}

      {busy ? (
        <div className="text-sm text-muted-foreground py-6">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No emails yet. {entityType ? "Send or link a thread to this record to see it here." : "Compose one, or connect your Gmail and hit Refresh."}
        </div>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="emails-list">
          {threads.map((t) => (
            <ThreadRow key={t.id} t={t} onOpen={() => setOpenThread(t.id)} />
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground">{total} thread{total === 1 ? "" : "s"}</div>

      {openThread && (
        <ThreadDialog wsId={activeWorkspaceId} tid={openThread} onClose={() => { setOpenThread(null); load(); }} />
      )}
      {composeOpen && (
        <ComposeDialog wsId={activeWorkspaceId} entityType={entityType} entityId={entityId}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); load(); }} />
      )}
    </div>
  );
}


function ThreadRow({ t, onOpen }) {
  const last = (t.last_activity_at || "").slice(0, 10);
  const preview = (t.participants || []).slice(0, 3).map((p) => p.name || p.email || p.phone).join(", ");
  const hasIn = (t.in_count || 0) > 0;
  const sentiment = t.ai_analysis?.sentiment;
  const risks = t.ai_analysis?.risk_flags || [];
  const channelKind = t.channel || "gmail";
  const channelLabel = channelKind === "whatsapp" ? "WhatsApp"
    : channelKind === "resend_inbound" ? "System email"
    : "Gmail";
  const channelClass = channelKind === "whatsapp"
    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : channelKind === "resend_inbound"
    ? "border-sky-300 bg-sky-50 text-sky-800"
    : "border-border bg-background text-muted-foreground";
  return (
    <button onClick={onOpen}
      className="w-full text-left px-3 py-2.5 hover:bg-muted/40 transition flex items-start gap-3"
      data-testid={`thread-row-${t.id}`}>
      <div className={`w-2 h-2 mt-1.5 rounded-full ${hasIn ? "bg-blue-500" : "bg-neutral-300"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{t.subject || "(no subject)"}</span>
          <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${channelClass}`} data-testid={`thread-channel-${channelKind}`}>
            {channelLabel}
          </span>
          <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">
            {t.message_count || 0} msg
          </span>
          {t.inherited_from_user_id && (
            <span className="text-[10px] uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5" title="Inherited from an off-boarded teammate">
              Inherited
            </span>
          )}
          {sentiment && sentiment !== "neutral" && (
            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
              sentiment === "angry" || sentiment === "frustrated" ? "border-red-300 bg-red-50 text-red-800" :
              sentiment === "delighted" || sentiment === "positive" ? "border-emerald-300 bg-emerald-50 text-emerald-800" :
              "border-border bg-background text-muted-foreground"
            }`}>
              {sentiment}
            </span>
          )}
          {risks.slice(0, 2).map((rf) => (
            <span key={rf} className="text-[10px] uppercase tracking-widest border border-red-200 bg-red-50 text-red-700 px-1.5 py-0.5">
              {rf.replace(/_/g, " ")}
            </span>
          ))}
          {(t.links || []).map((lk) => (
            <span key={lk.id} className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${lk.auto_linked ? "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800" : "border-primary/30 bg-primary/5 text-primary"}`}
                  data-testid={`thread-link-chip-${lk.entity_type}${lk.auto_linked ? "-auto" : ""}`}
                  title={lk.auto_linked ? `Auto-linked from ${lk.auto_linked_reason || "sender identity"}` : "Linked"}>
              {lk.auto_linked ? "✨ " : ""}{lk.entity_type}: {(lk.entity_label || "").slice(0, 24)}
            </span>
          ))}
        </div>
        <div className="text-xs text-muted-foreground truncate">{preview}</div>
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">{last}</div>
    </button>
  );
}


function ThreadDialog({ wsId, tid, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/email-threads/${tid}`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load thread"); }
    finally { setBusy(false); }
  }, [wsId, tid]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post(`/workspaces/${wsId}/email-threads/${tid}/refresh`);
      toast.success(`Fetched ${r.messages_added || 0} new`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const deleteThread = async () => {
    if (!window.confirm("Delete this entire thread permanently? Requires email.delete permission.")) return;
    try {
      await api.delete(`/workspaces/${wsId}/email-threads/${tid}`);
      toast.success("Thread deleted");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="thread-dialog">
        <DialogHeader>
          <DialogTitle className="pr-8">
            {busy ? "Loading…" : (data?.thread?.subject || "(no subject)")}
          </DialogTitle>
        </DialogHeader>
        {data && (
          <div className="space-y-3">
            {data.thread.inherited_from_user_id && (
              <div className="border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-2" data-testid="inherited-banner">
                <span className="text-[10px] uppercase tracking-widest border border-amber-400 bg-amber-100 px-1.5 py-0.5 mt-0.5">Inherited</span>
                <div>
                  This thread was reassigned to you after a teammate was off-boarded.
                  Read the history and take over — replying continues the conversation with your Gmail account.
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-border">
              <span className="text-xs text-muted-foreground">
                {data.thread.message_count} messages · Last: {(data.thread.last_activity_at || "").slice(0, 16).replace("T", " ")}
              </span>
              <Button variant="outline" size="sm" className="rounded-none h-7 ml-auto" onClick={refresh} disabled={refreshing} data-testid="thread-refresh">
                <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button variant="ghost" size="sm" className="rounded-none h-7 text-red-600" onClick={deleteThread} data-testid="thread-delete">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>

            <ThreadAiPanel wsId={wsId} tid={tid} thread={data.thread} onDraftUse={(draft) => { setReplyDraft(draft); setReplyOpen(true); }} />

            <ThreadLinks wsId={wsId} tid={tid} links={data.links} onChange={load} />

            <div className="space-y-4">
              {(data.messages || []).map((m) => (
                <MessageCard key={m.id} m={m} wsId={wsId} />
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          {data?.thread && (
            <Button className="rounded-none mr-auto" variant="outline"
                    onClick={() => setReplyOpen(true)}
                    data-testid="thread-reply">
              Reply
            </Button>
          )}
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
      {replyOpen && data?.thread && (data.thread.channel && data.thread.channel !== "gmail") && (
        <ChannelReplyDialog wsId={wsId} tid={tid} thread={data.thread}
          initialText={replyDraft ? stripHtml(replyDraft) : ""}
          onClose={() => setReplyOpen(false)}
          onSent={() => { setReplyOpen(false); load(); }} />
      )}
      {replyOpen && data?.thread && (!data.thread.channel || data.thread.channel === "gmail") && (
        <ComposeDialog wsId={wsId}
          initial={{
            to: (data.messages?.slice(-1)[0]?.from_email) || "",
            subject: (data.thread.subject || "").startsWith("Re:") ? data.thread.subject : `Re: ${data.thread.subject || ""}`,
            html: replyDraft || "",
          }}
          onClose={() => setReplyOpen(false)}
          onSent={() => { setReplyOpen(false); load(); }} />
      )}
    </Dialog>
  );
}


function ThreadAiPanel({ wsId, tid, thread, onDraftUse }) {
  const [ai, setAi] = useState(thread.ai_analysis || null);
  const [busy, setBusy] = useState(false);
  const staleCount = (thread.message_count || 0) - (ai?.analyzed_message_count || 0);
  const run = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/email-threads/${tid}/ai-analyze`, null, { params: { force } });
      setAi(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "AI analysis failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="ai-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest bg-indigo-600 text-white px-2 py-0.5">Claude AI</span>
        <span className="text-sm font-semibold">Thread intelligence</span>
        <div className="ml-auto flex items-center gap-2">
          {ai && staleCount > 0 && (
            <span className="text-[10px] uppercase tracking-widest text-amber-700">{staleCount} new msg since last analysis</span>
          )}
          <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => run(!!ai)} disabled={busy} data-testid="ai-run">
            {busy ? "Analyzing…" : (ai ? "Re-analyze" : "Ask AI")}
          </Button>
        </div>
      </div>
      {ai?.summary ? (
        <div className="space-y-2">
          <div className="text-sm">{ai.summary}</div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {ai.sentiment && (
              <span className={`px-2 py-0.5 uppercase tracking-widest text-[10px] border ${
                ai.sentiment === "angry" || ai.sentiment === "frustrated" ? "border-red-300 bg-red-50 text-red-800" :
                ai.sentiment === "delighted" || ai.sentiment === "positive" ? "border-emerald-300 bg-emerald-50 text-emerald-800" :
                "border-border bg-background text-muted-foreground"
              }`}>{ai.sentiment}</span>
            )}
            {(ai.risk_flags || []).map((rf) => (
              <span key={rf} className="px-2 py-0.5 uppercase tracking-widest text-[10px] border border-red-200 bg-red-50 text-red-700">{rf.replace(/_/g, " ")}</span>
            ))}
          </div>
          {ai.next_action && (
            <div className="text-xs bg-white border border-border p-2">
              <span className="font-semibold uppercase tracking-widest text-[10px] text-muted-foreground">Next action </span>
              {ai.next_action}
            </div>
          )}
          {ai.suggested_reply && (
            <details className="border border-border bg-white">
              <summary className="cursor-pointer px-2 py-1.5 text-xs font-semibold hover:bg-muted/50">Suggested reply (click to expand)</summary>
              <div className="p-3 text-sm max-h-56 overflow-y-auto" dangerouslySetInnerHTML={{ __html: ai.suggested_reply }} />
              <div className="border-t border-border px-2 py-1.5 flex justify-end">
                <Button size="sm" className="rounded-none h-7" onClick={() => onDraftUse(ai.suggested_reply)} data-testid="ai-use-draft">
                  Use draft
                </Button>
              </div>
            </details>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Click <strong>Ask AI</strong> to get a summary, sentiment, risk flags, and a draft reply for this thread.</p>
      )}
    </div>
  );
}


function MessageCard({ m, wsId }) {
  const [showFull, setShowFull] = useState(false);
  const time = (m.sent_at || "").slice(0, 16).replace("T", " ");
  const isOut = m.direction === "out";
  return (
    <div className={`border p-3 ${isOut ? "border-primary/30 bg-primary/5" : "border-border bg-background"}`} data-testid={`message-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${isOut ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          {isOut ? "Sent" : "Received"}
        </span>
        <span className="text-sm font-medium">{m.from_name || m.from_email}</span>
        <span className="text-xs text-muted-foreground">&lt;{m.from_email}&gt;</span>
        <span className="text-xs text-muted-foreground ml-auto">{time}</span>
      </div>
      <div className="text-xs text-muted-foreground mb-2">
        To: {(m.to || []).map((x) => x.email).join(", ")}
        {(m.cc || []).length > 0 && <> · Cc: {m.cc.map((x) => x.email).join(", ")}</>}
      </div>
      {showFull ? (
        <div className="prose prose-sm max-w-none bg-white border border-border p-3" dangerouslySetInnerHTML={{ __html: m.body_html || m.body_text?.replace(/\n/g, "<br/>") || "(empty)" }} />
      ) : (
        <div className="text-sm text-muted-foreground italic mb-2">
          {m.snippet || (m.body_text || "").slice(0, 220)}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <Button variant="ghost" size="sm" className="rounded-none h-6 text-xs px-2" onClick={() => setShowFull((s) => !s)} data-testid={`msg-toggle-${m.id}`}>
          {showFull ? "Hide" : "Show full"}
        </Button>
        {(m.attachments || []).map((a) => (
          <AttachmentBtn key={a.id} a={a} wsId={wsId} />
        ))}
      </div>
    </div>
  );
}


function AttachmentBtn({ a, wsId }) {
  const dl = async () => {
    try {
      const res = await api.get(`/workspaces/${wsId}/email-attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement("a");
      el.href = url; el.download = a.filename;
      document.body.appendChild(el); el.click(); el.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e) || "Download failed"); }
  };
  return (
    <Button variant="outline" size="sm" className="rounded-none h-6 text-xs px-2" onClick={dl} data-testid={`att-${a.id}`}>
      <Paperclip className="w-3 h-3 mr-1" /> {a.filename}
      <Download className="w-3 h-3 ml-1" />
    </Button>
  );
}


function ThreadLinks({ wsId, tid, links, onChange }) {
  const [adding, setAdding] = useState(false);
  const [entityType, setEntityType] = useState("client");
  const [options, setOptions] = useState([]);
  const [entityId, setEntityId] = useState("");

  useEffect(() => {
    if (!adding) return;
    const path = ({
      client: "clients", brand: "brands", project: "projects",
      invoice: "invoice-payments/invoices", lead: "leads",
    })[entityType];
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/${path}`);
        setOptions(Array.isArray(data) ? data : (data.items || data.rows || data.invoices || []));
      } catch { setOptions([]); }
    })();
  }, [entityType, adding, wsId]);

  const addLink = async () => {
    if (!entityId) { toast.error("Pick a record to link"); return; }
    const opt = options.find((o) => o.id === entityId) || {};
    try {
      await api.post(`/workspaces/${wsId}/email-threads/${tid}/links`, {
        entity_type: entityType, entity_id: entityId,
        entity_label: opt.name || opt.title || opt.invoice_no || undefined,
      });
      toast.success("Linked");
      setAdding(false); setEntityId("");
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e) || "Link failed"); }
  };

  const removeLink = async (lid) => {
    try {
      await api.delete(`/workspaces/${wsId}/email-threads/${tid}/links/${lid}`);
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e) || "Unlink failed"); }
  };

  return (
    <div className="border border-border bg-muted/20 p-2 space-y-2" data-testid="thread-links">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Linked to</span>
        {(links || []).map((lk) => (
          <span key={lk.id} className="inline-flex items-center gap-1 text-xs border border-border bg-background px-1.5 py-0.5" data-testid={`link-${lk.entity_type}-${lk.entity_id}`}>
            {lk.entity_type}: {lk.entity_label || lk.entity_id.slice(0, 8)}
            <button onClick={() => removeLink(lk.id)} className="hover:text-red-600 ml-1"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {!adding && (
          <Button variant="ghost" size="sm" className="rounded-none h-6 text-xs" onClick={() => setAdding(true)} data-testid="add-link-btn">
            <Link2 className="w-3 h-3 mr-1" /> Add link
          </Button>
        )}
      </div>
      {adding && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={entityType} onValueChange={(v) => { setEntityType(v); setEntityId(""); }}>
            <SelectTrigger className="h-8 w-32 rounded-none" data-testid="link-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="project">Project</SelectItem>
              <SelectItem value="invoice">Invoice</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityId} onValueChange={setEntityId}>
            <SelectTrigger className="h-8 min-w-[220px] rounded-none" data-testid="link-record">
              <SelectValue placeholder="Pick a record…" />
            </SelectTrigger>
            <SelectContent>
              {options.slice(0, 200).map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name || o.title || o.invoice_no || o.id.slice(0, 8)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="rounded-none h-8" onClick={addLink} data-testid="save-link">Link</Button>
          <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}


// ─────────────── Compose ───────────────

function ComposeDialog({ wsId, entityType, entityId, onClose, onSent, initial }) {
  const [to, setTo] = useState(initial?.to || "");
  const [cc, setCc] = useState(initial?.cc || "");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [html, setHtml] = useState(initial?.html || "");
  // R47 · Rich text by default; toggle for raw HTML power-users.
  const [htmlMode, setHtmlMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [status, setStatus] = useState(null); // {connected, account_email}
  const [shared, setShared] = useState(null);  // shared-mailbox config
  const [sendVia, setSendVia] = useState("gmail"); // 'gmail' | 'shared'

  useEffect(() => {
    (async () => {
      try {
        const { data: st } = await api.get(`/workspaces/${wsId}/oauth/google/status`);
        setStatus(st);
      } catch { setStatus({ connected: false }); }
      try {
        const { data } = await api.get(`/workspaces/${wsId}/email-templates`);
        setTemplates(data.templates || []);
      } catch { /* silent */ }
      try {
        const { data: sm } = await api.get(`/workspaces/${wsId}/shared-mailbox`);
        setShared(sm);
      } catch { /* silent */ }
    })();
  }, [wsId]);

  // Auto-pick default send-via when only ONE option is available.
  useEffect(() => {
    const sharedReady = shared?.configured && shared?.verified_at;
    const gmailReady = status?.connected;
    if (sharedReady && !gmailReady) setSendVia("shared");
    else if (gmailReady && !sharedReady) setSendVia("gmail");
  }, [status?.connected, shared?.configured, shared?.verified_at]);

  const applyTemplate = async (tid) => {
    setTemplateId(tid);
    if (!tid) return;
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setSubject(t.subject || "");
    setHtml(t.body || "");
    // If bound to an entity, prefill known variables best-effort
    if (entityType && entityId) {
      try {
        const { data } = await api.post(`/workspaces/${wsId}/email-templates/render`, {
          template_id: tid,
          variables: { entity_type: entityType, entity_id: entityId },
        });
        setSubject(data.subject || t.subject || "");
        setHtml(data.html || t.body || "");
      } catch { /* fine */ }
    }
  };

  const send = async () => {
    if (!to.trim()) { toast.error("Recipient email required"); return; }
    if (!subject.trim()) { toast.error("Subject required"); return; }
    if (!html.trim()) { toast.error("Message body required"); return; }
    setBusy(true);
    try {
      const linkTo = (entityType && entityId) ? [{ entity_type: entityType, entity_id: entityId }] : undefined;
      const ccArr = cc.trim() ? cc.split(",").map((s) => s.trim()).filter(Boolean) : undefined;
      if (sendVia === "shared") {
        const payload = { to: to.trim(), subject: subject.trim(), html, cc: ccArr, link_to: linkTo };
        await api.post(`/workspaces/${wsId}/shared-mailbox/send`, payload);
        toast.success(`Sent from shared mailbox (${shared.address})`);
      } else {
        const payload = { to: to.trim(), subject: subject.trim(), html, cc: ccArr, link_to: linkTo };
        const { data } = await api.post(`/workspaces/${wsId}/gmail/send`, payload);
        toast.success(`Sent from ${data.from}`);
      }
      onSent && onSent();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="compose-dialog"
                     description="Compose and send an email — from your Gmail or the shared workspace mailbox.">
        <DialogHeader><DialogTitle>Compose email</DialogTitle></DialogHeader>

        {(() => {
          const sharedReady = shared?.configured && shared?.verified_at;
          const gmailReady = status?.connected;
          const bothReady = sharedReady && gmailReady;
          if (!gmailReady && !sharedReady) {
            return (
              <div className="border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="compose-not-connected">
                You haven&apos;t connected Gmail and no verified shared mailbox is configured. Go to <a href="/app/integrations" className="underline">Integrations</a>.
              </div>
            );
          }
          if (bothReady) {
            return (
              <div className="border-b border-border pb-2 flex items-center gap-2 flex-wrap" data-testid="compose-send-via">
                <span className="label-eyebrow">Send from:</span>
                <button onClick={() => setSendVia("gmail")}
                        className={`px-2 py-1 text-xs border ${sendVia === "gmail" ? "border-foreground bg-foreground text-background" : "border-border"}`}
                        data-testid="compose-via-gmail">Gmail · {status.account_email}</button>
                <button onClick={() => setSendVia("shared")}
                        className={`px-2 py-1 text-xs border ${sendVia === "shared" ? "border-foreground bg-foreground text-background" : "border-border"}`}
                        data-testid="compose-via-shared">Shared · {shared.address}</button>
              </div>
            );
          }
          if (sharedReady && !gmailReady) {
            return (
              <div className="text-xs text-muted-foreground border-b border-border pb-2" data-testid="compose-shared-only">
                Sending from shared mailbox <strong>{shared.address}</strong>. Connect your Gmail in Integrations to also send from your personal account.
              </div>
            );
          }
          // gmail only
          return (
            <div className="text-xs text-muted-foreground border-b border-border pb-2">
              Sending from <strong>{status.account_email}</strong>. Every send is logged in Acquilix and will survive an off-boarding.
            </div>
          );
        })()}

        <div className="space-y-3">
          {templates.length > 0 && (
            <div>
              <label className="label-eyebrow block mb-1">Template (optional)</label>
              <Select value={templateId} onValueChange={applyTemplate}>
                <SelectTrigger className="h-10 rounded-none" data-testid="compose-template">
                  <SelectValue placeholder="— pick a template —" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-1">To *</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com" className="h-10 rounded-none" data-testid="compose-to" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Cc</label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cfo@example.com, ops@example.com" className="h-10 rounded-none" data-testid="compose-cc" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Subject *</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-10 rounded-none" data-testid="compose-subject" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label-eyebrow block">Message *</label>
              <label className="flex items-center gap-1 text-[10px] cursor-pointer text-muted-foreground">
                <input type="checkbox" checked={htmlMode} onChange={(e) => setHtmlMode(e.target.checked)} data-testid="compose-html-mode" />
                Edit raw HTML
              </label>
            </div>
            {htmlMode ? (
              <Textarea rows={10} value={html} onChange={(e) => setHtml(e.target.value)}
                          placeholder="<p>Hello…</p>" className="font-mono text-xs rounded-none" data-testid="compose-html" />
            ) : (
              <RichTextEditor value={html} onChange={setHtml} placeholder="Write your message…" />
            )}
          </div>
          {entityType && entityId && (
            <div className="text-xs text-muted-foreground border border-dashed border-border p-2">
              This email will be automatically linked to <strong>{entityType}</strong> record after sending.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={busy || (!status?.connected && !(shared?.configured && shared?.verified_at))} data-testid="compose-send">
            {busy ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Simple HTML→text stripper for pre-filling channel replies from an AI draft.
function stripHtml(html) {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
}


// Lightweight reply dialog for non-Gmail channels (WhatsApp / Resend inbound).
// Routes through the same messaging_service used by outbound flows.
// When `thread` isn't passed, self-fetches by tid so it can be triggered
// from anywhere that only knows the thread id (e.g. Client Timeline).
export function ChannelReplyDialog({ wsId, tid, thread: threadProp, initialText, onClose, onSent }) {
  const [thread, setThread] = useState(threadProp || null);
  const [text, setText] = useState(initialText || "");
  const [busy, setBusy] = useState(false);
  const [loadingThread, setLoadingThread] = useState(!threadProp);

  useEffect(() => {
    if (thread) return;
    let cancelled = false;
    api.get(`/workspaces/${wsId}/email-threads/${tid}`)
      .then((r) => { if (!cancelled) setThread(r.data.thread); })
      .catch((e) => { if (!cancelled) { toast.error(apiErrorMessage(e, "Failed to load thread")); onClose(); } })
      .finally(() => { if (!cancelled) setLoadingThread(false); });
    return () => { cancelled = true; };
  }, [wsId, tid, thread, onClose]);

  const channel = thread?.channel || "gmail";
  const isWa = channel === "whatsapp";
  const recipient = ((thread?.participants) || [])
    .map((p) => p.phone || p.email || p.name)
    .filter(Boolean)
    .join(", ");

  const send = async () => {
    if (!text.trim()) { toast.error("Type a message first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/email-threads/${tid}/reply-channel`,
        { body_text: text, subject: thread?.subject || "" }
      );
      const overall = data?.delivery?.overall || "sent";
      if (overall === "sent") {
        toast.success(isWa ? "WhatsApp sent" : "Email sent");
      } else if (overall === "skipped") {
        toast.warning("Recorded locally — provider not configured. Configure it under Comms Center → Flows.");
      } else {
        toast.error("Provider returned an error — check delivery health.");
      }
      onSent();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Reply failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="channel-reply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Reply · {isWa ? "WhatsApp" : "System email"}
            <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground font-normal">
              {channel}
            </span>
          </DialogTitle>
        </DialogHeader>
        {loadingThread ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading thread…</div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              To: <span className="font-mono">{recipient || "(unknown)"}</span>
            </div>
            <Textarea
              placeholder={isWa ? "Type your WhatsApp reply…" : "Type your email reply…"}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              className="rounded-none font-normal"
              data-testid="channel-reply-textarea"
            />
            <div className="text-[10px] text-muted-foreground">
              {isWa
                ? "Routes via your configured Interakt (falling back to Twilio) provider."
                : "Routes via your configured Resend provider."}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button className="rounded-none" onClick={send} disabled={busy || loadingThread || !thread}
                  data-testid="channel-reply-send">
            {busy ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
